/**
 * <complete-the-look>
 * ---------------------------------------------------------------------------
 * Native "Complete the look" upsell. Collects every ticked row and adds them
 * to the cart in a SINGLE /cart/add.js request, then either opens the theme's
 * native cart drawer (drawer mode) or redirects to /cart (page mode) — matching
 * whatever the store's cart type setting is.
 *
 * On the product page it also hides its own items from the theme's product
 * recommendation carousels ("You may also like") so the same product never
 * appears twice on the page.
 *
 * Self-contained: rendered by snippets/complete-the-look.liquid. No build step.
 */
class CompleteTheLook extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-add-selected]');
    this.rows = Array.from(this.querySelectorAll('.complete-the-look__row'));

    if (!this.button) return;

    this.addEventListener('change', () => this.syncButton());
    this.button.addEventListener('click', () => this.addSelected());
    this.syncButton();

    if (this.dataset.context === 'product') {
      this.dedupeRecommendations();
    }
  }

  get checkedRows() {
    return this.rows.filter((row) => {
      const checkbox = row.querySelector('.complete-the-look__checkbox');
      return checkbox && checkbox.checked && !checkbox.disabled;
    });
  }

  syncButton() {
    this.button.disabled = this.checkedRows.length === 0;
  }

  selectedItems() {
    return this.checkedRows.map((row) => {
      const select = row.querySelector('.complete-the-look__variant-select');
      const checkbox = row.querySelector('.complete-the-look__checkbox');
      const id = Number(select ? select.value : checkbox.dataset.variantId);
      return { id, quantity: 1 };
    });
  }

  // ---- Remove this box's products from the theme recommendation carousels ----------------------
  dedupeRecommendations() {
    const paths = new Set(
      this.rows.map((row) => row.dataset.productUrl).filter(Boolean)
    );
    if (paths.size === 0) return;

    const prune = () => {
      document.querySelectorAll('product-recommendations product-card').forEach((card) => {
        const link = card.querySelector('a[href*="/products/"]');
        if (!link) return;
        const path = link.getAttribute('href').split('?')[0];
        if (paths.has(path)) card.remove();
      });
    };

    // Recommendations load asynchronously, so prune now and again whenever they re-render.
    prune();
    document.querySelectorAll('product-recommendations').forEach((el) => {
      new MutationObserver(prune).observe(el, { childList: true, subtree: true });
    });
  }

  async addSelected() {
    const items = this.selectedItems().filter((item) => item.id);
    if (items.length === 0) return;

    this.button.setAttribute('aria-busy', 'true');
    this.button.disabled = true;

    // Ask the theme which sections need re-rendering (the cart drawer registers itself here).
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });

      if (!response.ok) throw await response.json();
      const added = await response.json();

      const settings = (window.themeVariables && window.themeVariables.settings) || {};

      // Page mode (or anywhere on the cart page): a full reload is the theme's own behaviour.
      if (settings.cartType === 'page' || settings.pageType === 'cart') {
        window.location.href = `${window.Shopify.routes.root}cart`;
        return;
      }

      // Drawer mode: fetch the full cart, attach the freshly rendered sections, and fire the
      // native event the <cart-drawer> listens for so it opens and re-renders itself.
      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      cart.sections = added.sections;

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart },
        })
      );
    } catch (error) {
      console.error('[complete-the-look] add to cart failed', error);
      // Let the cart re-sync so the UI is never left in a stale state.
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } finally {
      this.button.removeAttribute('aria-busy');
      this.syncButton();
    }
  }
}

if (!window.customElements.get('complete-the-look')) {
  window.customElements.define('complete-the-look', CompleteTheLook);
}
